import React, { useEffect, useMemo, useState, Suspense } from "react";
import {
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    Stack,
    Typography,
} from "@mui/material";

export default function OnboardingBlankState({
                                                 endpointUrl = "/api/steps",
                                                 fetchOptions,
                                                 title = "Get set up",
                                                 renderModalContent,
                                                 buttonLabel = "Continue",
                                                 onError,
                                             }) {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeStep, setActiveStep] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    const refresh = async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await fetch(endpointUrl, { method: "GET", ...fetchOptions });
            if (!res.ok) throw new Error(`Request failed: ${res.status}`);
            const data = await res.json();
            const next = {
                step1: Boolean(data?.step1),
                step2: Boolean(data?.step2),
                step3: Boolean(data?.step3),
            };
            setStatus(next);
        } catch (e) {
            const msg = e?.message || "Failed to load";
            setError(msg);
            if (onError) onError(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
    }, [endpointUrl]);

    const firstIncomplete = useMemo(() => {
        if (!status) return null;
        if (!status.step1) return 1;
        if (!status.step2) return 2;
        if (!status.step3) return 3;
        return null;
    }, [status]);

    const openForStep = (step) => {
        setActiveStep(step);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
    };

    const StepCard = ({ n, headline, description, completed }) => {
        const disabledStyles = completed
            ? { opacity: 0.35, pointerEvents: "none", filter: "grayscale(0.2)" }
            : {};

        return (
            <Card
                elevation={completed ? 0 : 2}
                sx={{
                    bgcolor: "#121212",
                    borderRadius: 3,
                    border: "1px solid rgba(255,255,255,0.06)",
                    ...disabledStyles,
                }}
            >
                <CardActionArea disableRipple>
                    <CardContent>
                        <Stack direction="row" spacing={3} alignItems="center">
                            <Typography
                                variant="h1"
                                sx={{
                                    fontSize: { xs: 56, sm: 64, md: 80 },
                                    lineHeight: 1,
                                    fontWeight: 800,
                                    color: "#fff",
                                    letterSpacing: -2,
                                }}
                            >
                                {String(n).padStart(2, "0")}
                            </Typography>
                            <Box>
                                <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700 }}>
                                    {headline}
                                </Typography>
                                {description ? (
                                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.72)" }}>
                                        {description}
                                    </Typography>
                                ) : null}
                            </Box>
                        </Stack>
                    </CardContent>
                </CardActionArea>
            </Card>
        );
    };

    return (
        <Box
            sx={{
                bgcolor: "#edd1c5",
                color: "#fff",
                borderRadius: 4,
                p: { xs: 2, sm: 3, md: 4 },
                border: "1px solid rgba(255,255,255,0.08)",
            }}
        >
            <Stack spacing={2}>
                <Typography variant="h5" sx={{ fontWeight: 800, color:'#333' }}>
                    Get started with Digitals
                </Typography>

            </Stack>

            <Box sx={{ mt: 3 }}>
                {loading && (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={20} />
                        <Typography variant="body2">Loading status…</Typography>
                    </Stack>
                )}
                {error && (
                    <Typography variant="body2" sx={{ color: "#ff6b6b" }}>
                        {error}
                    </Typography>
                )}
            </Box>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: 2 }}>
                <Box flex={1}>
                    <StepCard
                        n={1}
                        headline="Create your brand & annotate your logo"
                        description="Define your brand profile and upload/annotate your logo."
                        completed={Boolean(status?.step1)}
                    />
                </Box>
                <Box flex={1}>
                    <StepCard
                        n={2}
                        headline="Subscribe to your channels"
                        description="Connect and subscribe to the channels you plan to use."
                        completed={Boolean(status?.step2)}
                    />
                </Box>
                <Box flex={1}>
                    <StepCard
                        n={3}
                        headline="Create a project"
                        description="Spin up your first project to start working."
                        completed={Boolean(status?.step3)}
                    />
                </Box>
            </Stack>

            <Box sx={{ mt: 3 }}>
                {firstIncomplete ? (
                    <Stack spacing={1}>
                        <Divider sx={{ borderColor: "rgba(255,255,255,0.12)" }} />
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
                            <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.72)" }}>
                                Next up: Step {firstIncomplete}
                            </Typography>
                            <Button
                                variant="contained"
                                size="large"
                                onClick={() => openForStep(firstIncomplete)}
                                sx={{
                                    textTransform: "none",
                                    fontWeight: 700,
                                    borderRadius: 2,
                                }}
                            >
                                {buttonLabel}
                            </Button>
                        </Stack>
                    </Stack>
                ) : (
                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.72)" }}>
                        All steps completed! 🎉
                    </Typography>
                )}
            </Box>

            {activeStep && renderModalContent
                ? renderModalContent(activeStep, closeModal, refresh)
                : null}
        </Box>
    );
}
